import { camelize, decamelize } from "@ridi/object-case-converter";

export function snakeToSentence(str?: string | null) {
    if (!str) {
        return "";
    }

    return capitalize(str.split("_").join(" "));
}

export function snakeToTitle(str: string) {
    return str.split("_").map(capitalize).join(" ");
}

export function camelToSentence(str: string) {
    if (!str) {
        return "";
    }

    return capitalize(
        str
            .split(/(?=[A-Z])/)
            .join(" ")
            .toLowerCase(),
    );
}

export function capitalize(str?: string): string {
    if (!str) {
        return "";
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function snakeToCamel(str: string) {
    const parts = str.split("_");
    const start = parts[0];
    return start + parts.slice(1).map(capitalize).join("");
}

export function snakeToPascal(str: string) {
    return capitalize(snakeToCamel(str));
}

export function camelToSnake(str: string) {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Request keys whose value is a stored document rather than a set of Api
 * fields: the server keeps it verbatim, so its own keys must reach it exactly
 * as they were written. Decamelizing into them silently renames what the
 * server then fails to find — `floorPerDay` arriving as `floor_per_day` reads
 * as "not sent" and the Patch answers 200 having stored a floor of nothing.
 *
 * Only the value is protected; the key itself is still converted, so
 * `volumePolicy` still goes out as `volume_policy`.
 */
export const JSONB_KEYS = [
    // The Api doc's own request schemas.
    "properties",
    // The tracking plan's stored shapes, written camelCase (plan.md, "Data
    // model") and versioned verbatim into a plan version's content.
    "detection",
    "parameters",
    "expectations",
    "volumePolicy",
    // An audience's, funnel's or report's filters: management-api stores them
    // verbatim and hands them to data-api, which reads camelCase only, so
    // `includeEmpty` arriving as `include_empty` is dropped and the saved
    // filter runs without it. The company list sends the same document to
    // data-api directly and never saw the difference.
    "filters",
];

// Recursive function to convert object keys to snake case
export const objectKeysToSnake = (
    input: unknown,
    recursiveExcludes?: string[],
) =>
    decamelize(input, {
        recursive: recursiveExcludes ? { excludes: recursiveExcludes } : true,
    });

export const objectKeysToCamel = (
    input: unknown,
    recursiveExcludes?: string[],
) =>
    camelize(input, {
        recursive: recursiveExcludes ? { excludes: recursiveExcludes } : true,
    });

export const objectKeysToKebab = (input: Record<string, unknown>) => {
    const result: Record<string, unknown> = {};
    Object.keys(input).forEach((key) => {
        result[camelToKebab(key)] = input[key];
    });

    return result;
};

const camelToKebab = (str: string): string =>
    str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase();
